import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useNotification } from '../context/NotificationContext';
import { formatNumberPersian, convertPersianToEnglish } from '../utils/mobileUtils';
import { formatPrice } from '../utils/priceUtils';
import { getImageUrl } from '../utils/urlUtils';
import { apiFetch } from '../utils/api';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { addToCart } = useCart();
  const { showNotification } = useNotification();
  const [product, setProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await apiFetch(`/api/products/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        const imagesResponse = await apiFetch(`/api/products/${id}/images`);
        if (imagesResponse.ok) {
          const images = await imagesResponse.json();
          const allImages = [];
          if (data.imageUrl) {
            allImages.push({ imageUrl: data.imageUrl, isMain: true });
          }
          if (images && images.length > 0) {
            allImages.push(...images.map(img => ({ ...img, isMain: false })));
          }
          setProductImages(allImages);
        } else {
          if (data.imageUrl) {
            setProductImages([{ imageUrl: getImageUrl(data.imageUrl), isMain: true }]);
          } else {
            setProductImages([]);
          }
        }
        setCurrentImageIndex(0);
      } else {
        navigate('/');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
      navigate('/');
    }
  };

  const getPlaceholderImage = (text = 'Product', width = 500, height = 500) => {
    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="${width}" height="${height}" fill="#f0f0f0"/>
        <text x="50%" y="50%" font-family="Vazirmatn, sans-serif" font-size="${Math.min(width, height) / 15}" fill="#999" text-anchor="middle" dominant-baseline="middle">${text}</text>
      </svg>
    `.trim();
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  if (loading) {
    return (
      <div className={`product-detail-container ${isDarkMode ? 'dark' : ''}`}>
        <div className="loading">در حال بارگذاری...</div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className={`product-detail-container ${isDarkMode ? 'dark' : ''}`}>
      <button className="back-button" onClick={() => navigate('/')}>→ بازگشت به لیست محصولات</button>
      <div className="product-detail">
        <div className="product-detail-image-container">
          <div className="product-detail-image">
            <img 
              src={productImages.length > 0 ? getImageUrl(productImages[currentImageIndex]?.imageUrl) : getPlaceholderImage(product.name, 500, 500)} 
              alt={product.name}
              onError={(e) => { e.target.src = getPlaceholderImage(product.name, 500, 500); }}
            />
          </div>
          {productImages.length > 0 && (
            <div className="product-image-navigation">
              <button className="image-nav-btn prev" onClick={() => setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length)} disabled={productImages.length <= 1}>→</button>
              <div className="image-indicators">
                {productImages.map((_, index) => (
                  <span key={index} className={`indicator ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)} />
                ))}
              </div>
              <button className="image-nav-btn next" onClick={() => setCurrentImageIndex((prev) => (prev + 1) % productImages.length)} disabled={productImages.length <= 1}>←</button>
            </div>
          )}
        </div>
        <div className="product-detail-info">
          <div className="product-category-badge">{product.category?.name || 'بدون دسته‌بندی'}</div>
          <h1>{product.name}</h1>
          <p className="product-description-full">{product.description}</p>
          <div className="product-detail-price">
            <span className="price-label">قیمت:</span>
            <span className="price-value">{formatPrice(product.price)}</span>
          </div>
          <div className="product-detail-stock">
            <span className={`stock-badge ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
              {product.stock > 0 ? `موجود (${formatNumberPersian(product.stock)} عدد)` : 'ناموجود'}
            </span>
          </div>
          <div className="product-actions">
            {product.stock > 0 && (
              <div className="quantity-selector">
                <label>تعداد:</label>
                <div className="quantity-controls">
                  <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="qty-btn">-</button>
                  <input
                    type="text"
                    value={formatNumberPersian(quantity)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9۰-۹]/g, '');
                      const numValue = parseInt(convertPersianToEnglish(value)) || 1;
                      setQuantity(Math.max(1, Math.min(product.stock, numValue)));
                    }}
                    className="qty-input"
                    placeholder="۱"
                  />
                  <button type="button" onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="qty-btn">+</button>
                </div>
              </div>
            )}
            <button 
              className="add-to-cart-btn" 
              disabled={product.stock === 0}
              onClick={() => {
                if (product.stock > 0) {
                  addToCart(product, quantity);
                  showNotification('محصول به سبد خرید اضافه شد', 'success');
                }
              }}
            >
              {product.stock > 0 ? 'افزودن به سبد خرید' : 'ناموجود'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
